/**
 * Server functions for CIH guides.
 *
 * Guides are coach-facing how-to content managed by admins. They are tagged by
 * `guideType` and `practiceStage`; the practice/CTA stage drive CBB CTA routing.
 * Lifecycle transitions delegate to content-lifecycle helpers.
 *
 * Aliases: guides, cih guides, guide crud, guide lifecycle
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { permissionMiddleware } from "~/lib/permission-middleware";
import { GuideType } from "~/lib/schemas/db-enums-cih";
import {
  createGuideInputSchema,
  updateGuideInputSchema,
  type GuideListRow,
  type GuideRow,
} from "~/lib/server/cih/guides";

// ============================================================================
// Types (re-exported from shared server module)
// ============================================================================

export type { GuideListRow, GuideRow } from "~/lib/server/cih/guides";

// ============================================================================
// Query Keys
// ============================================================================

export const guideQueryKeys = {
  all: ["guides"] as const,
  list: (status?: string) => ["guides", "list", status ?? "all"] as const,
  detail: (id: string) => ["guides", id] as const,
  published: (slug: string) => ["guides", "published", slug] as const,
  publishedAll: ["guides", "published", "all"] as const,
};

// ============================================================================
// Server Functions — Reads (delegate to shared server layer)
// ============================================================================

/**
 * List all guides (admin). Excludes draft-of rows by default.
 * Optionally filter by status and/or guideType.
 */
export const getGuidesFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(
    z
      .object({
        status: z.string().optional(),
        guideType: z.nativeEnum(GuideType).optional(),
      })
      .optional(),
  )
  .handler(async ({ data }): Promise<GuideListRow[]> => {
    const { listGuides } = await import("~/lib/server/cih/guides");
    return listGuides(data);
  });

/**
 * Get a single guide by ID (admin).
 */
export const getGuideFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }): Promise<GuideRow | null> => {
    const { getGuideById } = await import("~/lib/server/cih/guides");
    return getGuideById(data);
  });

/**
 * Get a published guide by slug (public — no auth required).
 */
export const getPublishedGuideFn = createServerFn({ method: "POST" })
  .validator(z.object({ slug: z.string() }))
  .handler(async ({ data }): Promise<GuideRow | null> => {
    const { getPublishedGuideBySlug } = await import("~/lib/server/cih/guides");
    return getPublishedGuideBySlug(data);
  });

/**
 * List all published guides (public — no auth required).
 */
export const getPublishedGuidesFn = createServerFn({
  method: "POST",
}).handler(async () => {
  const { getPublishedGuides } = await import("~/lib/server/cih/guides");
  return getPublishedGuides();
});

// ============================================================================
// Server Functions — Writes (delegate to shared server layer)
// ============================================================================

/**
 * Create a new guide (admin). authorId is set from the session.
 */
export const createGuideFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(createGuideInputSchema)
  .handler(async ({ data, context }): Promise<GuideRow> => {
    const { createGuide } = await import("~/lib/server/cih/guides");
    return createGuide(data, { userId: context.user.id });
  });

/**
 * Update guide fields (admin, partial update).
 */
export const updateGuideFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(updateGuideInputSchema)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { updateGuide } = await import("~/lib/server/cih/guides");
    return updateGuide(data);
  });

// ============================================================================
// Server Functions — Lifecycle (delegate to shared server layer)
// ============================================================================

/**
 * Admin self-publish a draft guide (skips review).
 */
export const publishGuideFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { publishGuide } = await import("~/lib/server/cih/guides");
    return publishGuide(data, { userId: context.user.id });
  });

/**
 * Archive a published guide.
 */
export const archiveGuideFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { archiveGuide } = await import("~/lib/server/cih/guides");
    return archiveGuide(data, { userId: context.user.id });
  });

/**
 * Create an editable draft of a published guide.
 */
export const createGuideDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ publishedId: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<GuideRow> => {
    const { createDraftOfPublishedGuide } =
      await import("~/lib/server/cih/guides");
    return createDraftOfPublishedGuide(data, { userId: context.user.id });
  });

/**
 * Promote a draft back onto its published parent.
 */
export const promoteGuideDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ draftId: z.string().uuid() }))
  .handler(async ({ data }): Promise<GuideRow> => {
    const { promoteGuideDraft } = await import("~/lib/server/cih/guides");
    return promoteGuideDraft(data);
  });

/**
 * Restore an archived guide to draft.
 */
export const restoreGuideFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { restoreGuide } = await import("~/lib/server/cih/guides");
    return restoreGuide(data, { userId: context.user.id });
  });

/**
 * Approve an in_review guide (publish it).
 */
export const approveGuideFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { approveGuide } = await import("~/lib/server/cih/guides");
    return approveGuide(data, { userId: context.user.id });
  });

/**
 * Return an in_review guide to draft with feedback.
 */
export const returnGuideToDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid(), note: z.string().min(1) }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { returnGuideToDraft } = await import("~/lib/server/cih/guides");
    return returnGuideToDraft(data, { userId: context.user.id });
  });

/**
 * Submit a guide draft for review.
 */
export const submitGuideForReviewFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { submitGuideForReview } = await import("~/lib/server/cih/guides");
    return submitGuideForReview(data, { userId: context.user.id });
  });

// ============================================================================
// Query Options
// ============================================================================

export const guidesQueryOptions = (status?: string) =>
  queryOptions({
    queryKey: guideQueryKeys.list(status),
    queryFn: () => getGuidesFn({ data: { status } }),
  });

export const guideQueryOptions = (id: string) =>
  queryOptions({
    queryKey: guideQueryKeys.detail(id),
    queryFn: () => getGuideFn({ data: { id } }),
  });

export const publishedGuideQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: guideQueryKeys.published(slug),
    queryFn: () => getPublishedGuideFn({ data: { slug } }),
  });

export const publishedGuidesQueryOptions = () =>
  queryOptions({
    queryKey: guideQueryKeys.publishedAll,
    queryFn: () => getPublishedGuidesFn(),
  });
